import { cva, type VariantProps } from 'class-variance-authority'
import { Megaphone, Sparkles, TriangleAlert, X } from 'lucide-react'
import React from 'react'

import { cn } from '../../utils/cn'
import { Button } from './button'

const bannerVariants = cva('border', {
  variants: {
    variant: {
      // Semantic tones — for contained banners inside page content.
      error: 'border-error/35 bg-error/10 text-(--red-text)',
      warning: 'border-(--amber)/40 bg-(--amber)/15 text-(--amber-text)',
      info: 'border-brand-600 bg-brand-200 text-brand-900',
      // Announcement tones — solid dark fills for full-width bars above the
      // nav. Light ink-50 text on a large fill (per the ink-ramp rule).
      black: 'border-ink-900 bg-ink-900 text-ink-50',
      green: 'border-brand-900 bg-brand-900 text-ink-50',
      neutral: 'border-ink-500 bg-ink-500 text-ink-50',
    },
    small: {
      true: 'px-3 py-2.5',
      false: 'px-4.5 py-3.5',
    },
    // Full-width bleeds edge-to-edge: drop the rounding and the side/top
    // borders so the tinted background reaches both viewport edges, leaving
    // only a bottom border to separate it from whatever sits below (e.g. the
    // top nav). Contained keeps the rounded, fully-bordered card look.
    fullWidth: {
      true: 'rounded-none border-x-0 border-t-0',
      false: 'rounded-sm',
    },
  },
  compoundVariants: [
    // Full-width bars sit above the nav as a thin strip — override the
    // vertical padding the `small` flag would otherwise apply so the bar
    // stays skinny.
    { fullWidth: true, class: 'py-1.5' },
  ],
  defaultVariants: {
    variant: 'info',
    small: false,
    fullWidth: false,
  },
})

const ICONS: Record<NonNullable<VariantProps<typeof bannerVariants>['variant']>, typeof Sparkles> = {
  error: TriangleAlert,
  warning: TriangleAlert,
  info: Sparkles,
  black: Megaphone,
  green: Megaphone,
  neutral: Megaphone,
}

interface BannerProps extends VariantProps<typeof bannerVariants> {
  children: React.ReactNode
  /** Overrides the variant's default icon. Pass a Lucide element — sized here. */
  icon?: React.ReactNode
  /** Call-to-action rendered on the right, e.g. a <Button>. */
  action?: React.ReactNode
  /** Keeps the action beside a short message on a phone, instead of dropping it to its own row. */
  inlineAction?: boolean
  /** `center` for a one-line message; the default pins the icon to the first line. */
  align?: 'start' | 'center'
  /** When set, renders a dismiss button on the right (after `action`). */
  onDismiss?: () => void
  className?: string
  /** Wraps the content, e.g. the app's page container, to keep it on the page's max-width grid. */
  container?: React.ComponentType<{ children: React.ReactNode }>
}

/**
 * Page-level message in two layouts. Contained (default) is a rounded,
 * bordered card that sits inside page content. Full-width (`fullWidth`) bleeds
 * its tinted background edge-to-edge with only a bottom border — mount it above
 * the top nav across the full viewport, and pass `container` to keep the
 * text aligned with the page's max-width grid.
 *
 * Three tones by urgency: error (red), warning (amber), info (announcement
 * green). Bold titles are inline text inside children.
 */
export function Banner({
  variant = 'info',
  small = false,
  fullWidth = false,
  icon,
  action,
  inlineAction = false,
  onDismiss,
  className,
  children,
  align = 'start',
  container,
}: BannerProps) {
  const Icon = ICONS[variant ?? 'info']

  const Wrapper = container ?? React.Fragment

  // Top-aligned, a Lucide glyph reads high against text-sm — drop it onto the first line's centre.
  const iconOffset = !fullWidth && align === 'start' && 'mt-1'

  // Icon width + the gap-3 beside it, so a wrapped action aligns with the message.
  // Padding, not margin: the wrapped action is full width, so a margin pushed it out of the box.
  // Spelled out rather than interpolated: Tailwind only emits classes it can read here.
  const hasIcon = icon === undefined || !!icon
  const smallScreenIndent = !hasIcon ? '' : small ? '@max-xl:pl-7' : '@max-xl:pl-8'

  return (
    <div role="status" className={cn('@container', bannerVariants({ variant, small, fullWidth }), className)}>
      <Wrapper>
        <div
          role="status"
          className={cn(
            'flex gap-3 leading-normal',
            // Full-width announcement bars center their content and run a size
            // smaller (text-xs); contained banners keep the message
            // left-aligned at text-sm, icon pinned to the top.
            fullWidth
              ? 'relative items-center justify-center text-center text-xs'
              : cn(
                  'text-sm',
                  align === 'center' ? 'items-center' : 'items-start',
                  // A narrow banner (a phone, or a docked chat) can't fit the message beside the CTA — the text
                  // would squeeze to a word per line — so it answers to its own width, not the window's.
                  action && !inlineAction && '@max-xl:flex-wrap'
                )
          )}
        >
          {icon !== undefined ? (
            icon && (
              <span
                aria-hidden="true"
                className={cn(
                  'shrink-0',
                  iconOffset,
                  fullWidth ? '[&_svg]:size-3' : small ? '[&_svg]:size-4' : '[&_svg]:size-5'
                )}
              >
                {icon}
              </span>
            )
          ) : (
            <Icon
              aria-hidden="true"
              className={cn('shrink-0', iconOffset, fullWidth ? 'size-3' : small ? 'size-4' : 'size-5')}
            />
          )}
          {/* min-w-0: a flex item defaults to min-width:auto, so a long
              unbreakable string (a vendor error, a URL) would widen the banner
              past its container instead of wrapping inside it. */}
          <div className={cn('min-w-0', !fullWidth && 'flex-1')}>{children}</div>
          {action ? (
            <div
              className={cn(
                'shrink-0',
                !fullWidth && !inlineAction && cn('@max-xl:basis-full', smallScreenIndent)
              )}
            >
              {action}
            </div>
          ) : null}
          {onDismiss ? (
            <Button
              type="button"
              variant="iconOnly"
              size="icon-sm"
              aria-label="Dismiss"
              onClick={onDismiss}
              className={cn(
                'hover:bg-ink/5 text-current opacity-60 hover:opacity-100',
                // Centered bars pin dismiss to the right edge so it doesn't
                // pull the message off-center; contained banners keep it inline.
                fullWidth ? 'absolute top-1/2 right-2 -translate-y-1/2' : '-mr-1.5'
              )}
            >
              <X className="size-4" />
            </Button>
          ) : null}
        </div>
      </Wrapper>
    </div>
  )
}
